import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  Divider,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Card,
  CardContent,
  Snackbar
} from '@mui/material';
import { 
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Check as CheckIcon,
  Remove as RemoveIcon,
  Create as CreateIcon
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';

interface Photo {
  id: string;
  filename: string;
  url: string;
  thumbnailUrl: string;
  title: string;
  description: string;
  takenAt: Date;
  uploadedAt: Date;
  location?: {
    name?: string;
    coordinates?: [number, number];
  };
  tags: string[];
  tripId?: string;
}

interface Album {
  id: string;
  title: string;
  description?: string;
  coverPhotoId?: string;
  photoIds?: string[];
  memberIds?: string[];
  createdBy?: string;
  createdAt?: any;
  isPublic?: boolean;
  tripId?: string;
  eventId?: string;
  source?: string;
}

interface EditPhotoDetailsDialogProps {
  open: boolean;
  photo: Photo | null;
  onClose: () => void;
  onSave: (
    photoId: string, 
    updates: { 
      title?: string; 
      description?: string; 
      location?: { 
        name?: string; 
        coordinates?: [number, number]; 
      } 
    }
  ) => Promise<void>;
}

const EditPhotoDetailsDialog: React.FC<EditPhotoDetailsDialogProps> = ({
  open,
  photo,
  onClose,
  onSave
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [locationName, setLocationName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Album management states
  const [userAlbums, setUserAlbums] = useState<Album[]>([]);
  const [currentAlbums, setCurrentAlbums] = useState<Album[]>([]);
  const [selectedAlbumIds, setSelectedAlbumIds] = useState<Set<string>>(new Set());
  const [loadingAlbums, setLoadingAlbums] = useState(false);
  const [albumsError, setAlbumsError] = useState('');
  const [addingToAlbums, setAddingToAlbums] = useState(false);
  
  // Create new album states
  const [showCreateAlbum, setShowCreateAlbum] = useState(false);
  const [newAlbumTitle, setNewAlbumTitle] = useState('');
  const [newAlbumDescription, setNewAlbumDescription] = useState('');
  const [newAlbumPublic, setNewAlbumPublic] = useState(false);
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  const { currentUser } = useUser();

  useEffect(() => {
    if (photo) {
      setTitle(photo.title || '');
      setDescription(photo.description || '');
      setLocationName(photo.location?.name || '');
      setError('');
      
      // Load user albums and current photo albums
      loadUserAlbums();
      loadCurrentPhotoAlbums();
    }
  }, [photo]);

  const loadUserAlbums = async () => {
    if (!currentUser) return;
    
    setLoadingAlbums(true);
    setAlbumsError('');
    
    try {
      const { getAuthHeaders } = await import('../../utils/auth');
      const headers = await getAuthHeaders();

      const response = await fetch(
         `${process.env.REACT_APP_API_URL}/api/albums/user/${currentUser.uid}`,
         { headers }
      );
      if (!response.ok) {
        throw new Error('Failed to load albums');
      }
      
      const data = await response.json();
      setUserAlbums(data.albums || []);
    } catch (err) {
      console.error('Error loading user albums:', err);
      setAlbumsError('Failed to load albums');
    } finally {
      setLoadingAlbums(false);
    }
  };

  const loadCurrentPhotoAlbums = async () => {
    if (!photo || !currentUser) return;
    
    try {
      // Find albums that contain this photo
      const { getAuthHeaders } = await import('../../utils/auth');
      const headers = await getAuthHeaders();

      const response = await fetch(
         `${process.env.REACT_APP_API_URL}/api/albums/user/${currentUser.uid}`,
         { headers }
      );
      if (!response.ok) return;
      
      const data = await response.json();
      const albumsWithPhoto = (data.albums || []).filter((album: Album) => 
        album.photoIds && album.photoIds.includes(photo.id)
      );
      
      setCurrentAlbums(albumsWithPhoto);
    } catch (err) {
      console.error('Error loading current photo albums:', err);
    }
  };

  const handleAddToAlbums = async () => {
     if (!photo || selectedAlbumIds.size === 0) return;

     setAddingToAlbums(true);

     try {
        const { getAuthHeaders } = await import('../../utils/auth');
        const headers = await getAuthHeaders();

        const promises = Array.from(selectedAlbumIds).map(async (albumId) => {
           const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/add-photos`,
              {
                 method: 'POST',
                 headers,
                 body: JSON.stringify({
                    photoIds: [photo.id],
                 }),
              }
           );

           if (!response.ok) {
              throw new Error(`Failed to add to album ${albumId}`);
           }
        });

        await Promise.all(promises);

        // Refresh current albums and clear selection
        await loadCurrentPhotoAlbums();
        setSelectedAlbumIds(new Set());

        setSnackbarMessage(`Photo added to ${selectedAlbumIds.size} album(s)`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
     } catch (err) {
        console.error('Error adding to albums:', err);
        setSnackbarMessage('Failed to add photo to albums');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
     } finally {
        setAddingToAlbums(false);
     }
  };

  const handleRemoveFromAlbum = async (albumId: string) => {
     if (!photo) return;

     try {
        const { getAuthHeaders } = await import('../../utils/auth');
        const headers = await getAuthHeaders();

        const response = await fetch(
           `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/remove-photo`,
           {
              method: 'POST',
              headers,
              body: JSON.stringify({
                 photoId: photo.id,
              }),
           }
        );

        if (!response.ok) {
           throw new Error('Failed to remove from album');
        }

        // Refresh current albums
        await loadCurrentPhotoAlbums();

        setSnackbarMessage('Photo removed from album');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
     } catch (err) {
        console.error('Error removing from album:', err);
        setSnackbarMessage('Failed to remove photo from album');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
     }
  };

  const handleCreateNewAlbum = async () => {
    if (!newAlbumTitle.trim() || !photo || !currentUser) return;
    
    setCreatingAlbum(true);
    
    try {
      const { getAuthHeaders } = await import('../../utils/auth');
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/albums`, {
         method: 'POST',
         headers,
         body: JSON.stringify({
            title: newAlbumTitle.trim(),
            description: newAlbumDescription.trim(),
            createdBy: currentUser.uid,
            photoIds: [photo.id],
            isPublic: newAlbumPublic,
         }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create album');
      }
      
      // Reset form and refresh albums
      setNewAlbumTitle('');
      setNewAlbumDescription('');
      setNewAlbumPublic(false);
      setShowCreateAlbum(false);
      
      await loadUserAlbums();
      await loadCurrentPhotoAlbums();
      
      setSnackbarMessage('Album created and photo added');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error creating album:', err);
      setSnackbarMessage('Failed to create album');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setCreatingAlbum(false);
    }
  };

  const handleSave = async () => {
    if (!photo) return;
    
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const updates: any = {
        title: title.trim(),
        description: description.trim()
      };
      
      // Only update location name if we have coordinates
      if (photo.location?.coordinates) {
        updates.location = {
          ...photo.location,
          name: locationName.trim()
        };
      } else if (locationName.trim()) {
        // If user adds a location name but there are no coordinates
        updates.location = {
          name: locationName.trim()
        };
      }
      
      await onSave(photo.id, updates);
      onClose();
    } catch (err) {
      console.error('Error saving photo details:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAlbumToggle = (albumId: string) => {
    const newSelected = new Set(selectedAlbumIds);
    if (newSelected.has(albumId)) {
      newSelected.delete(albumId);
    } else {
      newSelected.add(albumId);
    }
    setSelectedAlbumIds(newSelected);
  };

  // Filter out albums that already contain the photo
  const availableAlbums = userAlbums.filter(album => 
    !album.photoIds?.includes(photo?.id || '')
  );

  if (!photo) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Photo Details
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <img
                  src={photo.thumbnailUrl || photo.url}
                  alt={photo.title}
                  style={{ 
                    width: '100%', 
                    maxHeight: '200px', 
                    objectFit: 'contain', 
                    borderRadius: '4px'
                  }}
                />
              </Box>
              
              <TextField
                label="Title"
                fullWidth
                margin="normal"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                error={!!error && !title.trim()}
                helperText={(!title.trim() && error) ? 'Title is required' : ''}
                autoFocus
              />
              
              <TextField
                label="Description"
                fullWidth
                margin="normal"
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              
              <TextField
                label="Location Name"
                fullWidth
                margin="normal"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                helperText={
                  photo.location?.coordinates 
                    ? "Name for this location (e.g. 'Central Park, NYC')" 
                    : "Note: To set coordinates, use the location edit tool"
                }
              />
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Taken on: {photo.takenAt.toLocaleDateString()} at {photo.takenAt.toLocaleTimeString()}
              </Typography>
              
              {error && !error.includes('Title') && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                <PhotoLibraryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Album Management
              </Typography>
              
              {/* Current Albums */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    Current Albums ({currentAlbums.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {currentAlbums.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      This photo is not in any albums yet.
                    </Typography>
                  ) : (
                    <List dense>
                      {currentAlbums.map((album) => (
                        <ListItem key={album.id}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            <PhotoLibraryIcon />
                          </Avatar>
                          <ListItemText
                            primary={album.title}
                            secondary={`${album.photoIds?.length || 0} photos`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => handleRemoveFromAlbum(album.id)}
                              size="small"
                              color="error"
                            >
                              <RemoveIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>
              
              {/* Add to Albums */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    Add to Albums ({availableAlbums.length} available)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {loadingAlbums ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : albumsError ? (
                    <Alert severity="error">{albumsError}</Alert>
                  ) : availableAlbums.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No available albums. Create a new one below.
                    </Typography>
                  ) : (
                    <>
                      <List dense>
                        {availableAlbums.map((album) => (
                          <ListItem key={album.id}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedAlbumIds.has(album.id)}
                                  onChange={() => handleAlbumToggle(album.id)}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">{album.title}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {album.photoIds?.length || 0} photos
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                      
                      {selectedAlbumIds.size > 0 && (
                        <Box mt={2}>
                          <Button
                            variant="contained"
                            onClick={handleAddToAlbums}
                            disabled={addingToAlbums}
                            startIcon={addingToAlbums ? <CircularProgress size={16} /> : <AddIcon />}
                            fullWidth
                          >
                            Add to {selectedAlbumIds.size} Album(s)
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
              
              {/* Create New Album */}
              <Accordion expanded={showCreateAlbum} onChange={() => setShowCreateAlbum(!showCreateAlbum)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    <CreateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Create New Album
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box component="form" onSubmit={(e) => { e.preventDefault(); handleCreateNewAlbum(); }}>
                    <TextField
                      label="Album Title"
                      fullWidth
                      margin="normal"
                      value={newAlbumTitle}
                      onChange={(e) => setNewAlbumTitle(e.target.value)}
                      required
                    />
                    
                    <TextField
                      label="Description (optional)"
                      fullWidth
                      margin="normal"
                      multiline
                      rows={2}
                      value={newAlbumDescription}
                      onChange={(e) => setNewAlbumDescription(e.target.value)}
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newAlbumPublic}
                          onChange={(e) => setNewAlbumPublic(e.target.checked)}
                        />
                      }
                      label="Make album public"
                      sx={{ mt: 1 }}
                    />
                    
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={!newAlbumTitle.trim() || creatingAlbum}
                      startIcon={creatingAlbum ? <CircularProgress size={16} /> : <CreateIcon />}
                      sx={{ mt: 2 }}
                    >
                      Create Album & Add Photo
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            color="primary" 
            variant="contained"
            disabled={isSubmitting}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditPhotoDetailsDialog;
